/**
 * The traceability reads the dedicated Traceability tree needs beyond what
 * `phase4.ts` (traceCommit, getDeploymentChanges, getTraceCoverage) and
 * `delivery.ts` (listReleases, listDeployments as plain arrays) already
 * expose: a run's artifacts, a repository's environments, one release, one
 * environment, and deployment and release history with their filters and
 * the `Page<T>` envelope kept (register §5 risk 9).
 *
 * What is not here, because the contract does not have it at this commit
 * (DL-244 items 1 and 3, pass 2): `listDeployments` filters by `release_id`,
 * `commit_sha` and `rollback_of`; `listArtifacts` filtered by `digest` or
 * `commit_sha` over a repository rather than a run; `listServiceDeployments`;
 * `getServiceEnvironmentHead`; explicit lineage states on `CommitTrace`.
 */

import { expect, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type Artifact = Schemas['Artifact'];
export type ReleaseState = Schemas['ReleaseState'];
export type Environment = Schemas['Environment'];
export type TraceDeployment = Schemas['TraceDeployment'];
export type TraceRun = Schemas['TraceRun'];
export type TraceArtifact = Schemas['TraceArtifact'];
export type TraceRelease = Schemas['TraceRelease'];
export type TraceIncident = Schemas['TraceIncident'];
export type TracePullRequest = Schemas['TracePullRequest'];
export type TraceCommitSummary = Schemas['TraceCommit'];

interface RepoScope {
  orgId: string;
  repoId: string;
}

export interface LineagePage<T> {
  data: T[];
  next_cursor: string | null;
}

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): LineagePage<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

/** The artifacts a run uploaded. There is no repository-wide artifact list yet. */
export async function listArtifacts(scope: RepoScope, runId: string): Promise<Artifact[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/runs/{run_id}/artifacts', {
      params: { path: { ...repoPath(scope), run_id: runId } },
    }),
  );
  return paged<Artifact>(value).data;
}

export async function listEnvironments(scope: RepoScope): Promise<Environment[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/environments', {
      params: { path: repoPath(scope) },
    }),
  );
  return paged<Environment>(value).data;
}

export function getEnvironment(scope: RepoScope, environmentId: string): Promise<Environment> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/environments/{environment_id}', {
      params: { path: { ...repoPath(scope), environment_id: environmentId } },
    }),
  );
}

export function getRelease(scope: RepoScope, releaseId: string): Promise<Schemas['Release']> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/releases/{release_id}', {
      params: { path: { ...repoPath(scope), release_id: releaseId } },
    }),
  );
}

export function getLatestRelease(scope: RepoScope): Promise<Schemas['Release']> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/releases/latest', {
      params: { path: repoPath(scope) },
    }),
  );
}

/** Deployment history pages with `before` (an id), not a cursor. */
export interface DeploymentHistoryQuery {
  environment_id?: string;
  state?: Schemas['DeploymentState'];
  before?: string;
  limit?: number;
}

export async function listDeploymentHistory(
  scope: RepoScope,
  query: DeploymentHistoryQuery = {},
): Promise<LineagePage<Schemas['Deployment']>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/deployments', {
      params: { path: repoPath(scope), query },
    }),
  );
  return paged<Schemas['Deployment']>(value);
}

export interface ReleaseHistoryQuery {
  state?: Schemas['ReleaseState'];
  tag?: string;
  before?: string;
  limit?: number;
}

export async function listReleaseHistory(
  scope: RepoScope,
  query: ReleaseHistoryQuery = {},
): Promise<LineagePage<Schemas['Release']>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/releases', {
      params: { path: repoPath(scope), query },
    }),
  );
  return paged<Schemas['Release']>(value);
}
